import React from "react";
import { ExternalLink } from "lucide-react";

interface SearchResult {
  url: string;
  title: string;
  pageAge?: string;
  type: string;
}

interface WebSearchResultsBlockProps {
  results: SearchResult[];
}

const SearchResultItem: React.FC<{ result: SearchResult }> = ({ result }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <a
      href={result.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block no-underline"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div
            className={`line-clamp-2 wrap-break-word transition-colors ${
              isHovered ? "text-secondary-n-100" : "text-secondary-n-300"
            }`}
          >
            {result.title}
          </div>
          <div className="text-[10px] text-secondary-n-600 mt-0.5 truncate">
            {new URL(result.url).hostname}
          </div>
        </div>
        <ExternalLink
          className={`w-3 h-3 shrink-0 mt-0.5 transition-colors ${
            isHovered ? "text-secondary-n-400" : "text-secondary-n-600"
          }`}
        />
      </div>
    </a>
  );
};

export const WebSearchResultsBlock: React.FC<WebSearchResultsBlockProps> =
  React.memo(({ results }) => {
    if (!results || results.length === 0) {
      return null;
    }

    return (
      <div className="text-xs space-y-2 mt-3">
        <div className="text-secondary-n-500 font-medium mb-2">
          Found {results.length} result{results.length !== 1 ? "s" : ""}
        </div>
        <div className="space-y-2">
          {results.map((result, index) => (
            <SearchResultItem key={index} result={result} />
          ))}
        </div>
      </div>
    );
  });

WebSearchResultsBlock.displayName = "WebSearchResultsBlock";
